/**
 * FieldModeSessionsContent - Şirket genelindeki saha kayıtları sekmesi
 * (tarih aralığı + çalışan adı arama filtresi ile).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import fieldModeRequestService from '../../../../services/fieldModeRequest/fieldModeRequest.service';
import { FieldModeSession } from '../../../../types/fieldModeRequest.types';
import { handleApiError } from '../../../../utils/errorHandler';
import FieldModeSessionsTable from './FieldModeSessionsTable';

const { RangePicker } = DatePicker;

interface FieldModeSessionsContentProps {
  /** Değiştiğinde liste yeniden çekilir (sayfa başlığındaki Yenile butonu için) */
  refreshSignal?: number;
}

const FieldModeSessionsContent: React.FC<FieldModeSessionsContentProps> = ({ refreshSignal = 0 }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(6, 'day'), dayjs()]);
  const [search, setSearch] = useState('');
  const [sessions, setSessions] = useState<FieldModeSession[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fieldModeRequestService.getFieldModeSessions({
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
      });
      setSessions(Array.isArray(result) ? result : []);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions, refreshSignal]);

  const filteredSessions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    if (!term) return sessions;
    return sessions.filter((s) =>
      `${s.user.firstName} ${s.user.lastName}`.toLocaleLowerCase('tr-TR').includes(term)
    );
  }, [sessions, search]);

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <RangePicker
          format="DD.MM.YYYY"
          allowClear={false}
          value={range}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
          }}
        />
        <Input
          placeholder="Çalışan adı ara..."
          prefix={<SearchOutlined />}
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240, maxWidth: '100%' }}
        />
        <Button icon={<i className="ti ti-reload me-2" />} onClick={fetchSessions} loading={loading}>
          Yenile
        </Button>
        <span className="text-muted ms-auto" style={{ fontSize: '0.875rem' }}>
          {filteredSessions.length} kayıt
        </span>
      </div>

      <FieldModeSessionsTable sessions={filteredSessions} loading={loading} showEmployee />
    </div>
  );
};

export default FieldModeSessionsContent;
